import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, Mail, Check, RefreshCw, User, Phone } from "lucide-react";
import { toast } from "sonner";
import { FadeIn } from "@/components/site/FadeIn";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NewsletterSignup = () => {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [answer, setAnswer] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [captcha, setCaptcha] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const loadChallenge = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/subscribe/challenge`);
      setCaptcha(data);
      setAnswer("");
    } catch {
      setCaptcha(null);
    }
  }, []);

  useEffect(() => {
    loadChallenge();
  }, [loadChallenge]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !name.trim() || !captcha) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/subscribe`, {
        email: email.trim(),
        name: name.trim(),
        phone: phone.trim() ? `+${phone.trim()}` : "",
        captcha_token: captcha.token,
        captcha_answer: answer.trim(),
        website,
      });
      setDone(true);
      toast.success(data.message || "You're on the list.");
      setEmail("");
      setName("");
      setPhone("");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 400) {
        toast.error(typeof detail === "string" ? detail : "Please answer the question correctly.");
        loadChallenge();
      } else if (status === 429) {
        toast.error(typeof detail === "string" ? detail : "Too many attempts. Please try again later.");
      } else {
        toast.error(
          Array.isArray(detail) ? "Please enter a valid email address." : detail || "Something went wrong. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <FadeIn delay={0.2} className="mt-16 max-w-xl mx-auto">
      <div className="rounded-3xl border border-stone-200/80 bg-white/70 backdrop-blur-sm p-8 sm:p-10 text-center">
        <p className="text-xs uppercase tracking-[0.24em] text-[#2a7b76] font-semibold mb-3">
          Join the community
        </p>
        <p className="font-display text-2xl text-[#201c19] mb-6">
          Be a part of the work's evolution
        </p>

        {done ? (
          <div
            data-testid="newsletter-success"
            className="flex items-center justify-center gap-2 text-[#2a7b76] font-medium py-3"
          >
            <Check size={18} /> You're on the list — a welcome note is on its way.
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="relative">
              <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                data-testid="closing-newsletter-name"
                className="w-full rounded-full border border-stone-300 bg-white pl-11 pr-4 py-3.5 text-[#201c19] placeholder:text-stone-400 focus:outline-none focus:border-[#2a7b76] focus:ring-2 focus:ring-[#2a7b76]/20 transition"
              />
            </div>

            <div className="relative">
              <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="closing-newsletter-input"
                className="w-full rounded-full border border-stone-300 bg-white pl-11 pr-4 py-3.5 text-[#201c19] placeholder:text-stone-400 focus:outline-none focus:border-[#2a7b76] focus:ring-2 focus:ring-[#2a7b76]/20 transition"
              />
            </div>

            <div className="relative">
              <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
              <span className="absolute left-10 top-1/2 -translate-y-1/2 text-[#201c19] pointer-events-none">+</span>
              <input
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d\s().-]/g, ""))}
                placeholder="61 412 345 678 (optional)"
                aria-label="Phone number with country code"
                data-testid="closing-newsletter-phone"
                className="w-full rounded-full border border-stone-300 bg-white pl-[3.25rem] pr-4 py-3.5 text-[#201c19] placeholder:text-stone-400 focus:outline-none focus:border-[#2a7b76] focus:ring-2 focus:ring-[#2a7b76]/20 transition"
              />
            </div>
            <p className="-mt-1 pl-5 text-left text-xs text-stone-400">
              Include your country code — e.g. +61 (Australia), +1 (USA), +44 (UK).
            </p>

            {/* honeypot — hidden from humans, tempting to bots */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="absolute left-[-9999px] h-0 w-0 opacity-0"
            />

            <div className="flex items-center gap-3">
              <div className="flex-1 flex items-center gap-2 rounded-full border border-stone-300 bg-white px-4 py-3">
                <span data-testid="captcha-question" className="text-sm text-[#4b443e] whitespace-nowrap">
                  {captcha ? captcha.question : "Loading…"}
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  required
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="Answer"
                  data-testid="captcha-answer-input"
                  className="w-full min-w-0 bg-transparent text-[#201c19] placeholder:text-stone-400 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={loadChallenge}
                  data-testid="captcha-refresh-btn"
                  aria-label="New question"
                  className="text-stone-400 hover:text-[#2a7b76] transition-colors"
                >
                  <RefreshCw size={15} />
                </button>
              </div>
              <button
                type="submit"
                disabled={loading || !captcha}
                data-testid="closing-newsletter-submit"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#201c19] text-[#fdfbf7] font-semibold px-7 py-3.5 hover:bg-[#2a7b76] transition-colors duration-300 disabled:opacity-60 whitespace-nowrap"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : "Join"}
              </button>
            </div>
          </form>
        )}
        <p className="mt-4 text-xs text-stone-400">
          Occasional updates — no noise.
        </p>
      </div>
    </FadeIn>
  );
};

export default NewsletterSignup;
